<script>
import {ref, onMounted, onBeforeUnmount, toRef} from "vue";
import {Cropper} from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import "vue-advanced-cropper/dist/theme.compact.css";
import axios from "axios";
import {router} from "@inertiajs/vue3";

import {useSearchStore} from "@js/store/SearchStore.js";
import {useSwipePanel} from "@js/useSwipePanel.js";

import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import IconX from "@js/components/Icons/IconX.vue";
import ImageSearchResultItem from "@js/components/ImageSearch/ImageSearchResultItem.vue";
import IconCamera from "@js/components/Icons/IconCamera.vue";
import IconResize from "@js/components/Icons/IconResize.vue";
import CatalogProductSkeleton from "@js/components/Sections/Catalog/Components/CatalogProductSkeleton.vue";

export default {
    name: "ImageSearchResults",
    components: {
        CatalogProductSkeleton,
        IconResize,
        IconCamera,
        Cropper,
        OnlyMobile,
        IconX,
        ImageSearchResultItem,
    },
    data() {
        return {
            cropper_props: {
                aspectRatio: null,
                handlers: {
                    top: true,
                    right: true,
                    bottom: true,
                    left: true,
                    topLeft: true,
                    topRight: true,
                    bottomLeft: true,
                    bottomRight: true,
                    eastNorth: true,
                    north: true,
                    south: true,
                    west: true,
                    east: true,
                    westNorth: true,
                    westSouth: true,
                    eastSouth: true,
                },
            },
        };
    },
    setup() {
        const searchStore = useSearchStore();

        const modalRef = ref(null);
        const dragHandleRef = ref(null);
        const cropperRef = ref(null);
        const isMobilePanel = ref(false);

        const updateMobileState = () => {
            isMobilePanel.value = window.innerWidth < 1024;
        };

        const {
            rendered,
            panelStyle,
            backdropStyle,
            close,
            handleBackgroundClick,
        } = useSwipePanel({
            isOpen: toRef(searchStore.imageSearchResult, "isOpen"),
            panelRef: modalRef,
            handleRef: dragHandleRef,
            enabled: isMobilePanel,
            direction: "bottom",
            closeThreshold: 110,
            onClose: () => {
                searchStore.hideImageSearchResult();
            },
        });

        let removeRouterListener = null;

        onMounted(() => {
            updateMobileState();
            window.addEventListener("resize", updateMobileState);

            removeRouterListener = router.on("before", () => {
                searchStore.hideImageSearchResult();
            });
        });

        onBeforeUnmount(() => {
            window.removeEventListener("resize", updateMobileState);

            if (typeof removeRouterListener === "function") {
                removeRouterListener();
            }
        });

        const cropAndSearch = () => {
            const cropper = cropperRef.value;
            if (!cropper) return;

            const result = cropper.getResult();
            const canvas = result?.canvas;

            if (!canvas) return;

            canvas.toBlob((blob) => {
                if (!blob) return;

                const form = new FormData();
                form.append("photo", blob, "cropped.png");

                searchStore.resultsLoaded = false;

                axios.post("/axios/search/search-by-image", form, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }).then(({data}) => {
                    searchStore.imageSearch.results = data;
                    searchStore.resultsLoaded = true;
                }).catch((error) => {
                    console.error("Image search error:", error);
                    searchStore.resultsLoaded = true;
                });
            }, "image/png");
        };

        const reUpload = () => {
            searchStore.hideImageSearchResult();
            searchStore.showImageSearch();
        };

        return {
            searchStore,
            modalRef,
            dragHandleRef,
            cropperRef,
            isMobilePanel,
            rendered,
            panelStyle,
            backdropStyle,
            closeModal: close,
            handleBackgroundClick,
            cropAndSearch,
            reUpload,
        };
    },
};
</script>

<template>
    <div
        v-if="rendered"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        @click="handleBackgroundClick"
    >
        <div
            class="absolute inset-0 bg-black/70"
            :style="backdropStyle"
        ></div>

        <div
            ref="modalRef"
            :style="isMobilePanel ? panelStyle : {}"
            class="relative z-[1] bg-white lg:rounded-2xl shadow-lg lg:max-w-[1400px] lg:max-h-[90%] w-full h-full overflow-hidden"
            @click.stop
        >
            <button
                @click.prevent="closeModal"
                class="absolute top-2 right-2 text-gray-400 md:hover:text-black z-20"
            >
                <IconX class="h-7 w-7"/>
            </button>

            <OnlyMobile :maxSize="1024">
                <div class="absolute top-3 right-0 left-0 m-auto z-10 flex justify-center">
                    <div
                        ref="dragHandleRef"
                        class="h-8 w-24 flex items-start justify-center select-none"
                        style="touch-action: none; user-select: none;"
                    >
                        <div class="h-[5px] w-[50px] bg-gray-400 rounded-full"></div>
                    </div>
                </div>
            </OnlyMobile>

            <div class="w-full h-full p-5 overflow-y-auto mt-10 md:mt-5">
                <div class="grid grid-cols-12 gap-5">
                    <div class="col-span-12 md:col-span-3 sticky md:top-0 md:self-start md:z-10 max-md:border-b max-md:pb-5">
                        <p class="font-semibold text-lg mb-2">Поиск по фото</p>

                        <div class="w-full h-[300px] rounded-xl overflow-hidden">
                            <cropper
                                ref="cropperRef"
                                :src="searchStore.imageSearch.previewUrl"
                                :grid="true"
                                :stencil-props="cropper_props"
                                class="cropper w-full h-full"
                            />
                        </div>

                        <p class="text-sm md:text-xs font-bold text-gray-800 pt-2 pb-4">
                            Можете выбрать рамкой любой элемент на снимке
                        </p>

                        <button
                            @click.prevent="cropAndSearch"
                            class="flex items-center justify-center leading-tight gap-2 w-full py-3 bg-baseColor md:hover:bg-baseColor/80 text-black rounded text-sm font-semibold transition"
                        >
                            <IconResize class="w-5 h-5"/>
                            Обрезать и искать
                        </button>

                        <button
                            @click.prevent="reUpload"
                            class="flex items-center justify-center leading-tight gap-2 mt-2 w-full py-3 border rounded md:hover:bg-gray-100 text-sm font-semibold transition"
                        >
                            <IconCamera class="w-5 h-5"/>
                            Выбрать новое фото
                        </button>
                    </div>

                    <div class="col-span-12 md:col-span-9">
                        <p class="font-semibold text-lg mb-2">Результаты поиска:</p>

                        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 mb-10">
                            <ImageSearchResultItem
                                v-if="searchStore.resultsLoaded"
                                v-for="product in searchStore.imageSearch.results"
                                :key="product.id ?? product.slug"
                                :data="product"
                            />

                            <CatalogProductSkeleton
                                v-else
                                v-for="i in 6"
                                :key="i"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
